import { useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import Dialog from 'epn-ui/umd/dist/components/Dialog';
import Notification from 'epn-ui/umd/dist/components/Notification';

import useI18n from '@i18n';

import api from '@api';
import apiHooks from '@apiHooks';

import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from 'src/utils/metrics/amplitude';
import sendGoal from '@utils/metrics/ymSendGoal';
import sendGaEvent from 'src/utils/metrics/ga';

import { IGetTrafficSourcesDataFormatted, IGetOffersDataFormatted } from '@api/types';

import AddPlacementForm, { IAddPlacementForm } from './AddPlacementForm';

const { useTrafficSources } = apiHooks;

interface IModalAddPlacementProps {
    onClose: () => void;
    offer: IGetOffersDataFormatted;
    handleModalFinish: () => void;
}

const ModalAddPlacement: React.FC<IModalAddPlacementProps> = ({
    onClose,
    offer,
    handleModalFinish,
}) => {
    const [createInProgress, setCreateInProgress] = useState<boolean>(false);
    const { locale, t } = useI18n('Creatives');

    const { data: trafficSources, isLoading: TrafficSorcesIsLoading } = useTrafficSources<
        IGetTrafficSourcesDataFormatted[]
    >({
        dependencies: [locale],
    });

    /**
     * @param {IAddPlacementForm} formData
     * @returns {Promise<void>}
     */
    const onSubmitHandler = async (formData: IAddPlacementForm): Promise<void> => {
        try {
            setCreateInProgress(true);

            const create = await api.placement.addUserPlacement({
                body: {
                    url: formData.url,
                    offerId: formData.offer,
                    categoryId: formData.trafficSource,
                },
            });

            if (create.result) {
                Notification.success({ message: t('The site has been sent for moderation') });

                logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.addPlacement, {
                    offer_id: formData.offer,
                });

                sendGoal('add_placement_new', 89120816);

                sendGaEvent({
                    eventCategory: 'placements',
                    eventLabel: 'add',
                });

                handleModalFinish();
            }
        } catch (error) {
            if (error && error.response && typeof error.response.json === 'function') {
                const resp = await error.response.json();

                if (Array.isArray(resp.errors) && resp.errors.length !== 0) {
                    resp.errors.forEach(item => {
                        Notification.error({
                            message: t(
                                item.error_description || 'Failed to send the site for moderation',
                            ),
                        });
                    });
                } else {
                    Notification.error({ message: t('Failed to send the site for moderation') });
                }

                return;
            }

            Notification.error({ message: t('Failed to send the site for moderation') });

            Sentry.captureException(error, {
                extra: {
                    ...formData,
                },
            });
        } finally {
            setCreateInProgress(false);
        }
    };

    return (
        <Dialog open title={t('Add a placement')} onCancel={onClose}>
            <AddPlacementForm
                offers={[offer]}
                trafficSources={trafficSources}
                isLoading={TrafficSorcesIsLoading}
                createInProgress={createInProgress}
                onSubmit={onSubmitHandler}
            />
        </Dialog>
    );
};

export default ModalAddPlacement;
